import React from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import Card from '../../../component/card/card';
import { Template, TemplateItem2 } from '../../../models/printer-template.entity';
import { apiManage_rbp as apiManage } from '../../../request';
import _ from 'lodash';
import {
  ES_IMAGE,
  testData,
  TestDataType,
} from './constants';
import { isVerifactuArea, isTicketBAIArea } from '../../../constants/spain-city-area';
import GlobalNotification from '../../../component/common/snackbar';
import i18n from '../../../i18n';
import { Visibility, VisibilityOff } from '@mui/icons-material';

function alignNorm(align?: string): 'left' | 'center' | 'right' {
  const a = (align ?? 'LEFT').toString().toLowerCase();
  if (a === 'center') return 'center';
  if (a === 'right') return 'right';
  return 'left';
}

export default class ContentDetail extends React.Component<Prop, State> {
  public state: State = {
    activeKey: '0-0',
    showPreview: false,
  };

  newContent: TemplateItem2[] = [];

  verifactuStartIndex?: number;
  verifactuEndIndex?: number;
  tabiStartIndex?: number;
  tabiEndIndex?: number;

  // 重置当前高亮索引
  resetActiveKey(key?: string) {
    this.setState({ activeKey: key || '0-0' })
  }
  // 校验是否可以保存
  // 同一行 所有字段的 width + left <= 72
  canSave() {
    return !this.parseContent(this.props.content).some((subData: any, index: number) => {
      const total = subData.reduce((prev: number, item: any) => {
        return prev += ['Code', 'TAUX', 'HT', 'TVA', 'TTC'].includes(item.key) ? 0 : item.left + item.width
      }, 0)
      if (total > 72) {
        GlobalNotification.error(
          i18n.t('print_template_page_width_warning_info', { index: index + 1 }),
        )
        return true
      }
      return false
    })
  }
  // 重构发票单的内容
  rebuildContent(content: TemplateItem2[]) {
    const isSpain = this.props.country === 'Spain';
    const content17 = _.cloneDeep(content);
    const cifIndex = content17.findIndex(item => item.key === (isSpain ? 'binding_piva' : 'cif'));
    if (cifIndex > -1) {
      content17.splice(cifIndex, 2);
    }
    const nifIndex = content17.findIndex(item => item.key === (isSpain ? 'codice_destinatario' : 'nif'));
    if (nifIndex > -1) {
      content17.splice(nifIndex, 2);
    }
    if (this.props.country === 'Italy') {
      const taxIndex = content17.findIndex(item => item?.key === 'total_without_tax');
      if (taxIndex > -1) {
        let count = 1;
        for (let i = taxIndex + 1; i < content17.length; i++) {
          if (content17[i].value !== 'NEWLINE') break;
          count++;
        }
        content17.splice(taxIndex, count)
      }
    }
    this.newContent = content17;
    return content17
  }

  // 计算西班牙税务收据单中 Verifactu / TicketBAI 相关字段所在的行号区间
  private initSpainReceiptIndex(isPreviewMode?: boolean) {
    const currentTemplateId = this.props.template?.template_id || -1;
    // 仅对模版 24 / 26 计算行号
    if (![24, 26].includes(currentTemplateId)) return;

    const rows = isPreviewMode
      ? this.parsePreviewData()
      : this.parseContent(this.props.content);

    this.verifactuStartIndex = undefined;
    this.verifactuEndIndex = undefined;
    this.tabiStartIndex = undefined;
    this.tabiEndIndex = undefined;


    rows.forEach((subData: any, rowIndex: number) => {
      subData.forEach((item: any) => {
        // Verifactu: es_verifactu_title ~ es_verifactu_bottom
        if (item.key === 'es_verifactu_title') {
          if (this.verifactuStartIndex === undefined || rowIndex < this.verifactuStartIndex) {
            this.verifactuStartIndex = rowIndex;
          }
        }
        if (item.key === 'es_verifactu_bottom') {
          if (this.verifactuEndIndex === undefined || rowIndex > this.verifactuEndIndex) {
            this.verifactuEndIndex = rowIndex;
          }
        }

        // TicketBAI: es_tabi_bottom ~ es_tabi_image
        if (item.key === 'es_tabi_bottom') {
          if (this.tabiStartIndex === undefined || rowIndex < this.tabiStartIndex) {
            this.tabiStartIndex = rowIndex;
          }
        }
        if (item.key === 'es_tabi_image') {
          if (this.tabiEndIndex === undefined || rowIndex > this.tabiEndIndex) {
            this.tabiEndIndex = rowIndex;
          }
        }
      })
    })
  }

  /**
   * 判断当前行在西班牙税务收据单中是否需要隐藏
   * - 餐馆区域属于 Verifactu：显示 es_verifactu_title，es_verifactu_image，es_verifactu_bottom 对应行
   * - 餐馆区域属于 TicketBAI：显示 es_tabi_bottom，es_tabi_image 对应行
   * - 仅影响前端展示，不修改模板原始内容
   */
  shouldHideSpainReceiptRow(index: number) {
    // 不是西班牙税务收据单、发票单
    if (![24, 26].includes(this.props.template?.template_id || -1)) return false;

    const territory = this.props.taxInfo?.territory;
    const isVerifactu = isVerifactuArea(territory);
    const isTicketBAI = isTicketBAIArea(territory);

    // 餐馆区域不属于Verifactu，隐藏 es_verifactu_title ~ es_verifactu_bottom 所在区间
    if (!isVerifactu && this.verifactuStartIndex !== undefined && this.verifactuEndIndex !== undefined) {
      if (index >= this.verifactuStartIndex && index <= this.verifactuEndIndex) {
        return true;
      }
    }

    // 餐馆区域不属于TicketBAI，隐藏 es_tabi_bottom ~ es_tabi_image 所在区间
    if (!isTicketBAI && this.tabiStartIndex !== undefined && this.tabiEndIndex !== undefined) {
      if (index >= this.tabiStartIndex && index <= this.tabiEndIndex) {
        return true;
      }
    }

    return false;
  }


  // 解析模板
  parseContent(content: TemplateItem2[]) {
    const data: any = []
    let data2: any = []
    const newContent = this.props.template?.template_id === 17 ? this.rebuildContent(content) : content;
    const cb = (item: TemplateItem2) => {
      if (typeof item.value === 'string') {
        if (item.value === 'NEWLINE') {
          data.push(data2)
          data2 = []
        } else {
          data2.push(item)
        }
      } else {
        item.value.forEach(cb)
      }
    }
    newContent.forEach(cb)
    if (data2.length) data.push(data2)
    return data
  }

  // 解析预览数据
  parsePreviewData() {
    const data: any = []
    let data2: any = []
    const { content, headInfo, taxInfo, template } = this.props;
    const newContent = template?.template_id === 17 ? this.rebuildContent(content) : content;
    newContent.forEach(item => {
      if (typeof item.value === 'string') {
        if (item.value === 'NEWLINE') {
          data.push(data2)
          data2 = []
        } else {
          const _item = _.cloneDeep(item)
          // shell2 展示没有图片，显示图片占位
          if (this.props.LogoKey.includes(_item.key as string)) {
            _item.value = `<div style="background-color: #f0f0f0; width: ${item.logoWidth || 80}px;height:${item.logoHeight || 80}px" />`;
            data2.push(_item)
            return
          }
          // if (this.props.LogoKey.includes(_item.key as string)) {
          //   let src = `${LocalStorageManager.getLocalStorage(LOCAL_STORAGE_KEYS.SERVER_URL)}/qop/logo.jpg?v=${Date.now()}`;
          //   _item.key === 'es_verifactu_image' && (src = EsVerifactu);
          //   _item.key === 'es_tabi_image' && (src = EsTabi);
          //   _item.value = `<img src=${src} style="width: ${item.logoWidth || 80}px;height:${item.logoHeight || 80}px" />`
          //   data2.push(_item)
          //   return
          // }
          _item.value = (item.value as string)
            .replace(/%(.*)%/, ($1: string, $2: string) => {
              if ($2 === 'head') return headInfo;
              if ($2 === 'r_denominazione') return _.get(taxInfo, 'denominazione', '');
              if ($2 === 'r_indirizzo') return _.get(taxInfo, 'indirizzo', '');
              if ($2 === 'r_city') return _.get(taxInfo, 'city', '');
              if ($2 === 'r_province') return _.get(taxInfo, 'province', '');
              if ($2 === 'r_cap_comune_provincia') return _.get(taxInfo, 'cap', '');
              if ($2 === 'r_idcodice') return _.get(taxInfo, 'IdCodice', '');
              if ($2 === 'r_nif') return _.get(taxInfo, 'codiceFiscale', '');
              return ((testData[`${this.props.template!.template_id}` as TestDataType] || {}) as Record<string, any>)[$2] ?? $1;
            })
            .replace(/\n/g, '<br/>')
            .replace(/\s/g, '&nbsp;')

          const mulLine = _item.value.split('<br/>').filter(str => str)
          if (mulLine.length > 1) {
            mulLine.forEach(str => {
              const _item2 = _.cloneDeep(_item)
              _item2.value = str
              data.push([_item2])
            })
          } else {
            data2.push(_item)
          }
        }
      } else {
        (testData[this.props.template!.template_id] || {})[item.key as string]?.forEach(dish => {
          (item.value as TemplateItem2[]).forEach(item1 => {
            if (item1.value === 'NEWLINE') {
              data.push(data2)
              data2 = []
            } else {
              const _item = _.cloneDeep(item1)
              _item.value = (_item.value as string)
                .replace(/%(.*)%/, ($1: string, $2: string) => { return dish[$2 as 'name'] ?? $1 })
                .replace(/\s/g, '&nbsp;')
              data2.push(_item)
            }
          })
        })
      }
    })
    if (data2.length) data.push(data2)

    return data
  }

  public showPreview = () => {
    this.setState({ showPreview: !this.state.showPreview });
  }

  async onTestPrint() {
    const { headInfo, taxInfo, template, content, templateId } = this.props;
    const tid = Number(template?.template_id ?? templateId);
    const param = _.cloneDeep(testData[String(tid) as TestDataType] || {}) as Record<string, unknown>;
    _.set(param, 'head', headInfo ?? '');
    if (tid === 17) {
      _.set(param, 'r_denominazione', _.get(taxInfo, 'denominazione', ''));
      _.set(param, 'r_indirizzo', _.get(taxInfo, 'indirizzo', ''));
      _.set(param, 'r_city', _.get(taxInfo, 'city', ''));
      _.set(param, 'r_province', _.get(taxInfo, 'province', ''));
      _.set(param, 'r_cap_comune_provincia', _.get(taxInfo, 'cap', ''));
      _.set(param, 'r_idcodice', _.get(taxInfo, 'IdCodice', ''));
    }
    if (tid === 24 || tid === 26) {
      const territory = String(_.get(taxInfo, 'territory', '') ?? '');
      const isVerifactu = isVerifactuArea(territory);
      const isTicketBAI = isTicketBAIArea(territory);
      if (isVerifactu) {
        _.set(param, 'es_verifactu_image', ES_IMAGE.es_verifactu_image);
        _.unset(param, 'es_tabi_bottom');
        _.unset(param, 'es_tabi_image');
      } else if (isTicketBAI) {
        _.set(param, 'es_tabi_image', ES_IMAGE.es_tabi_image);
        _.unset(param, 'es_verifactu_title');
        _.unset(param, 'es_verifactu_image');
        _.unset(param, 'es_verifactu_bottom');
      }
    }

    await apiManage.testPrintTemplate({
      params: param,
      template: {
        template_id: tid,
        template_content: tid === 17 ? this.newContent : content,
        template_name: String(template?.template_name ?? ''),
      },
      length_scale: String(template?.length_scale ?? ''),
      size_scale: String(template?.size_scale ?? ''),
    });
  }

  public getRealHeight = (arr: TemplateItem2[], isPreviewMode?: boolean): number => {
    return Math.max(
      ...arr.map((item) => {
        if (this.props.calcWidthRef && typeof item.value === 'string') {
          this.props.calcWidthRef.style.width = `${item.width || 72}mm`;
          this.props.calcWidthRef.style.wordBreak = 'break-all';
          this.props.calcWidthRef.innerHTML =
            isPreviewMode || !item.value.includes('%') ? item.value : '{xxx}';
          this.props.calcWidthRef.style.fontSize =
            item.size != null ? `${Number(item.size) * 1.2}px` : 'unset';
          this.props.calcWidthRef.style.fontWeight = item.bold ? 'bold' : 'unset';
          this.props.calcWidthRef.style.textDecoration = `${item.strikeout ? ' line-through' : ''}${item.underline ? ' underline' : ''
            }`;
          this.props.calcWidthRef.style.fontStyle = item.italic ? 'italic' : 'unset';
          this.props.calcWidthRef.style.minWidth = !item.width ? 'max-content' : '';
          return this.props.calcWidthRef.getBoundingClientRect().height;
        }
        return 20;
      }),
      20,
    );
  };

  public renderContent = (data: TemplateItem2[][], isPreviewMode?: boolean): React.ReactNode => {
    const tid = String(this.props.templateId);
    this.initSpainReceiptIndex(isPreviewMode);
    return (
      <>
        {data.map((subData, index) =>
          this.shouldHideSpainReceiptRow(index) ? null : (
            <Box
              style={{ position: 'relative', height: this.getRealHeight(subData, isPreviewMode), display: 'flex' }}
              key={`row-${tid}-${index}`}
              className={`row-${tid}-${index}`}
            >
              {subData.map((item, index2) => {

                return (
                  <Box
                    component="div"
                    key={`${index}-${index2}`}
                    className={`template-field ${this.state.activeKey === `${index}-${index2}` ? 'selected' : ''} ${index}`}
                    style={{
                      position:
                        item.left || alignNorm(item.align as string | undefined) !== 'left'
                          ? 'absolute'
                          : undefined,
                      left:
                        alignNorm(item.align as string | undefined) === 'center'
                          ? '50%'
                          : alignNorm(item.align as string | undefined) === 'right'
                            ? '100%'
                            : `${item.left || 0}mm`,
                      transform:
                        alignNorm(item.align as string | undefined) === 'center'
                          ? 'translateX(-50%)'
                          : alignNorm(item.align as string | undefined) === 'right'
                            ? 'translateX(-100%)'
                            : 'unset',
                      fontSize: item.size != null ? `${Number(item.size)}px` : 'unset',
                      fontWeight: item.bold ? 'bold' : 'unset',
                      textDecoration: `${item.strikeout ? ' line-through' : ''}${item.underline ? ' underline' : ''}`,
                      fontStyle: item.italic ? 'italic' : '',
                      width: `${item.width || 0}mm`,
                      minWidth: !item.width ? 'max-content' : '',
                      fontFamily: item.font,
                      textAlign: item.textAlign || 'left'
                    }}
                    onClick={() => {
                      if (isPreviewMode) return
                      this.props.onClickItem(item)
                      this.setState({ activeKey: `${index}-${index2}` })
                    }}
                    dangerouslySetInnerHTML={{
                      __html: !isPreviewMode && ((item.value as string).match(/%(.*)%/) || this.props.LogoKey.includes(item.key as string)) ? '{xxx}' : item.value as string
                    }}
                  />
                );
              })}
            </Box>
          )
        )}
      </>
    );
  };

  render() {
    const { showPreview } = this.state;
    const outerStyle = { marginLeft: 0, flex: 'none' as const, marginBottom: 0 };

    /* 与 RBP2 一致：小票宽 80mm，`left`/`width` 使用 mm 定位 */
    const style: any = { width: '80mm', padding: '0 4mm', wordBreak: 'break-all', boxSizing: 'content-box' };
    return (

      <Box className="print-template-page__column print-template-page__column--preview">
        <Box className="print-template-page__column-head">
          <Typography component="h2" className="print-template-page__column-head-title">
            {i18n.t('print_template_page_live_preview_mm', { mm: this.props.paperMm })}
          </Typography>
          <IconButton
            className="print-template-page__preview-icon"
            aria-label={i18n.t('global_btn_preview')}
            onClick={() => {
              this.setState({ showPreview: !this.state.showPreview })
            }}
            size="medium"
          >
            {this.state.showPreview ? (
              <Visibility sx={{ fontSize: 24 }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 24 }} />
            )}
          </IconButton>
        </Box>
        <Grid item style={outerStyle}>
          <Card
            className={`content-card`}
            contentRender={
              showPreview ?
                <Box component="div" style={{ ...style, overflow: 'hidden', }}>
                  {this.renderContent(this.parsePreviewData(), true)}
                </Box> :
                <Box component="div" style={style}>
                  {this.renderContent(this.parseContent(this.props.content))}
                </Box>
            }
          />
        </Grid>
      </Box>

    );
  }
}

type Prop = {
  content: TemplateItem2[];
  onClickItem: Function;
  calcWidthRef: HTMLElement | null;
  templateId: TestDataType;
  width?: number;
  embedded?: boolean;
  /** 当前模板（测试打印 scale / 名称；西班牙行号用 template_id） */
  template?: Template | null;
  headInfo?: string;
  taxInfo: any;  // 税务信息
  country?: string;
  /** 与 RBP2 `LogoKey`：Logo / 税务图字段 */
  logoFieldKeys?: readonly string[];
  // 图片key
  LogoKey: string[];
  paperMm: number;
};

type State = {
  activeKey: string;
  showPreview: boolean;
};
